import {
  Col,
  Row,
  Space,
  Table,
  Tag,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
} from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ReloadOutlined, FileAddOutlined } from "@ant-design/icons";
import { HEADERS_EXPORT_BEN, POST_MAIN_LAWYER } from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";
import UpdateFinalCaseDoc from "./modalFinalCase/UpdateFinalCaseDoc";

const { Option } = Select;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));

  // States
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState("11");
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);

  // ✅ โหลดข้อมูลหลัก
  const loadMain = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();

    try {
      const res = await axios.post(
        POST_MAIN_LAWYER,
        { userId: userId },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );

      if (res.data && res.data.length > 0) {
        const filteredData = res.data.filter(
          (item) => item.currentStatus === 11 || item.currentStatus === 12,
        );
        setDataArr(filteredData);
      } else {
        setDataArr([]);
        message.info("ไม่มีข้อมูล");
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading data:", error);
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถโหลดข้อมูลได้";
        message.error(errorMsg);
        setDataArr([]);
      }
    } finally {
      setLoading(false);
    }

    return () => {
      controller.abort();
    };
  }, [userId]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  // ✅ ฟังก์ชันสำหรับสร้าง Options ของ Select
  const statusOptions = useMemo(
    () => [
      { value: "99", icon: "🗂️", label: "ทั้งหมด" },
      { value: "11", icon: "🕒", label: "รอดําเนินการ" },
      { value: "12", icon: "📜", label: "คดีถึงที่สุดแล้ว" },
    ],
    [],
  );

  // ✅ กรองข้อมูลอัตโนมัติด้วย useMemo
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const searchQuery = normalize(searchInputTable);

    return dataArr.filter((item) => {
      const passQuery =
        !searchQuery ||
        [
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      let passStatus = true;
      if (processStatus && processStatus !== "99") {
        const statusValues = processStatus.split(",").map(Number);
        passStatus = statusValues.includes(item.currentStatus);
      }

      return passQuery && passStatus;
    });
  }, [dataArr, searchInputTable, processStatus]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    setProcessStatus("99");
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);

  // ✅ Reload หลังบันทึกสำเร็จ
  const handleReload = useCallback(() => {
    loadMain();
  }, [loadMain]);

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "center",
      render: (_, record) => (
        <span>
          {`${record.SNAM || ""}${record.NAME1 || ""} ${
            record.NAME2 || ""
          }`.trim()}
        </span>
      ),
    },
    {
      title: "วันพิพากษา",
      key: "dtJudge",
      align: "center",
      render: (_, record) =>
        record.dtJudge ? (
          <span>
            {dayjs(record.dtJudge).add(543, "year").format("DD MMM YY")}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      width: 160,
      render: (_, record) => {
        const { currentStatus } = record;

        const statusMap = {
          11: { color: "blue", label: "รอดำเนินการ" },
          12: { color: "green", label: "คดีถึงที่สุดแล้ว" },
        };

        const status = statusMap[currentStatus] || {
          color: "default",
          label: "ไม่ระบุ",
        };

        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
    {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        const { currentStatus } = record;

        if (currentStatus === 11) {
          return (
            <Button
              type="primary"
              ghost
              icon={<FileAddOutlined />}
              onClick={() => {
                setDataRecord(record);
                setIsModal(true);
              }}
            >
              ลงข้อมูลคดีถึงที่สุด
            </Button>
          );
        }

        return <span>-</span>;
      },
    },
  ].filter(Boolean);

  return (
    <Card>
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        <Row gutter={[0, 16]}>
          {/* Filters */}
          <Col span={24}>
            <Space size={10} wrap>
              <Select
                size="large"
                style={{ minWidth: 180 }}
                placeholder="เลือกสถานะ"
                value={processStatus}
                onChange={setProcessStatus}
              >
                {statusOptions.map(({ value, icon, label }) => (
                  <Option key={value} value={value}>
                    <span style={{ marginRight: 8 }}>{icon}</span>
                    {label}
                  </Option>
                ))}
              </Select>

              <Input
                placeholder="ค้นหาเลขสัญญา, ชื่อ"
                value={searchInputTable}
                onChange={(e) => setSearchInputTable(e.target.value)}
                style={{ width: 250, borderRadius: 8 }}
                size="small"
                allowClear
                suffix={<span style={{ marginRight: 5 }}>🔍</span>}
              />

              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilter}
                size="large"
                style={{
                  borderRadius: 8,
                  backgroundColor: "#fff1f0",
                  borderColor: "#ffa39e",
                  color: "#cf1322",
                }}
              >
                ล้างตัวกรอง
              </Button>
            </Space>
          </Col>

          {/* Table */}
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={arrayTable}
              rowKey={(record) => record.CONTNO || record.id}
              scroll={{ x: 1000 }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              footer={() => (
                <div style={{ textAlign: "left", fontWeight: 500 }}>
                  จำนวนสัญญาทั้งหมด: {arrayTable?.length || 0} รายการ
                </div>
              )}
            />
          </Col>
        </Row>
      </Spin>

      {isModal && (
        <UpdateFinalCaseDoc
          open={isModal}
          onCancel={setIsModal}
          dataDefault={dataRecord}
          onSuccess={handleReload}
        />
      )}
    </Card>
  );
};

const MainFinalCase = MotionHoc(Main);
export default MainFinalCase;
